import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Grid, GridColProps } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useSelector } from 'react-redux';

import { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { selectPatientSettings } from '@/store/selectors/transmissionReportPatientSelectors';
import { getIsPractitioner } from '@/store/selectors/transmissionReportSelectors';
import { useUpdatePatientSettingsMutation } from '@/store/slices/api/patientsApiSlice';
import { useGetPatientSettingsQuery } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';

import { LabelAndValue } from '@/types/custom';
import Dropdown from '@/ui/Dropdown';
import IconButton from '@/ui/IconButton';
import LabeledField from '@/ui/LabeledField';
import SkeletonGrid from '@/ui/Skeleton';

import styles from './SettingsSection.module.scss';

interface PatientSettings {
  hfMonitoring: boolean;
  hfOnlyMonitoring: boolean;
  alertOnlyMonitoring: boolean;
  cycleIcdPm: number;
  cycleHf: number;
  cycleIlr: number;
  inClinicBuffer: number;
}

interface FieldConfig {
  label: string;
  value: string;
  field: keyof PatientSettings;
  options: LabelAndValue[];
  currentValue: string;
  disabled?: boolean;
}

interface SettingsSectionProps {
  patientId: string;
  reportId?: number;
}

const YES_NO_OPTIONS: LabelAndValue[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

const HF_ILR_CYCLE_OPTIONS: LabelAndValue[] = [
  { value: '31', label: '31 Days' },
  { value: '35', label: '35 Days' },
  { value: '60', label: '60 Days' },
  { value: '91', label: '91 Days' },
  { value: '95', label: '95 Days' },
  { value: '180', label: '180 Days' }
];

const ICD_PM_CYCLE_OPTIONS: LabelAndValue[] = [
  { value: '91', label: '91 Days' },
  { value: '95', label: '95 Days' },
  { value: '180', label: '180 Days' },
  { value: '365', label: '365 Days' }
];

const BUFFER_OPTIONS: LabelAndValue[] = [
  { value: '14', label: '14 Days' },
  { value: '30', label: '30 Days' },
  { value: '60', label: '60 Days' }
];

const formatBoolean = (value: boolean): string => (value ? 'Yes' : 'No');
const formatCycle = (value: number): string => (value ? `${value} Days` : '-');

const processFieldValue = (value: string): boolean | number | string => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  const numValue = parseInt(value, 10);
  return isNaN(numValue) ? value : numValue;
};

const SettingsSection = ({ patientId }: SettingsSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localSettings, setLocalSettings] = useState<PatientSettings | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const isPractitioner = useAppSelector(getIsPractitioner);
  const patientSettings = useSelector((state: RootState) =>
    selectPatientSettings(state, patientId)
  );

  const { isLoading, isError } = useGetPatientSettingsQuery(patientId);
  const [updatePatientSettings] = useUpdatePatientSettingsMutation();

  useEffect(() => {
    if (patientSettings && !localSettings) {
      setLocalSettings({ ...patientSettings });
    }
  }, [patientSettings, localSettings]);

  const fieldConfigs = useMemo((): FieldConfig[] => {
    if (!localSettings) return [];

    return [
      {
        label: 'HF Monitoring',
        value: formatBoolean(localSettings.hfMonitoring),
        field: 'hfMonitoring',
        disabled: false,
        options: YES_NO_OPTIONS,
        currentValue: String(localSettings.hfMonitoring)
      },
      {
        label: 'HF Only Monitoring',
        value: formatBoolean(localSettings.hfOnlyMonitoring),
        field: 'hfOnlyMonitoring',
        disabled: false,
        options: YES_NO_OPTIONS,
        currentValue: String(localSettings.hfOnlyMonitoring)
      },
      {
        label: 'Alert Only Monitoring',
        value: formatBoolean(localSettings.alertOnlyMonitoring),
        field: 'alertOnlyMonitoring',
        options: YES_NO_OPTIONS,
        currentValue: String(localSettings.alertOnlyMonitoring)
      },
      {
        label: 'Cycle ICD/PM',
        value: formatCycle(localSettings.cycleIcdPm),
        field: 'cycleIcdPm',
        disabled: false,
        options: ICD_PM_CYCLE_OPTIONS,
        currentValue: String(localSettings.cycleIcdPm)
      },
      {
        label: 'Cycle HF',
        value: formatCycle(localSettings.cycleHf),
        field: 'cycleHf',
        disabled: false,
        options: HF_ILR_CYCLE_OPTIONS,
        currentValue: String(localSettings.cycleHf)
      },
      {
        label: 'Cycle ILR',
        value: localSettings.cycleIlr > 0 ? formatCycle(localSettings.cycleIlr) : '-',
        field: 'cycleIlr',
        disabled: false,
        options: HF_ILR_CYCLE_OPTIONS,
        currentValue: String(localSettings.cycleIlr)
      },
      {
        label: 'In Clinic Buffer',
        value: formatCycle(localSettings.inClinicBuffer),
        field: 'inClinicBuffer',
        options: BUFFER_OPTIONS,
        currentValue: String(localSettings.inClinicBuffer)
      }
    ];
  }, [localSettings]);

  const { monitoringFields, cycleFields } = useMemo(
    () => ({
      monitoringFields: fieldConfigs.slice(0, 3),
      cycleFields: fieldConfigs.slice(3)
    }),
    [fieldConfigs]
  );

  const handleFieldChange = useCallback((field: keyof PatientSettings, value: string) => {
    const processedValue = processFieldValue(value);
    setLocalSettings((prev) => (prev ? { ...prev, [field]: processedValue } : null));
  }, []);

  const handleSave = useCallback(async () => {
    if (!localSettings) return;

    setIsUpdating(true);
    try {
      await updatePatientSettings({ patientId, ...localSettings }).unwrap();

      notifications.show({
        title: 'Success',
        message: 'Patient settings updated successfully',
        color: 'green'
      });

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update settings:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update patient settings',
        color: 'red'
      });
    } finally {
      setIsUpdating(false);
    }
  }, [localSettings, patientId, updatePatientSettings]);

  const handleEdit = useCallback(() => setIsEditing(true), []);

  const createCustomEditableComponent = useCallback(
    (field: FieldConfig) => (
      <Dropdown
        options={field.options}
        value={field.options.find((opt) => opt.value === field.currentValue)}
        isDisabled={field.disabled || isUpdating}
        onChange={(val) => {
          if (val && !Array.isArray(val)) {
            handleFieldChange(field.field, val.value);
          }
        }}
      />
    ),
    [handleFieldChange, isUpdating]
  );

  const renderFieldGrid = useCallback(
    (fields: FieldConfig[], spanConfig: GridColProps['span'] = { base: 12, sm: 6, md: 3 }) => (
      <Grid>
        {fields.map((field) => (
          <Grid.Col key={field.field} span={spanConfig}>
            <LabeledField
              label={field.label}
              value={field.value}
              isEditable={isEditing && !isUpdating}
              customEditableComponent={
                isEditing && !isUpdating ? createCustomEditableComponent(field) : undefined
              }
            />
          </Grid.Col>
        ))}
      </Grid>
    ),
    [isEditing, isUpdating, createCustomEditableComponent]
  );

  if (isLoading || !patientId || !localSettings) {
    return (
      <>
        <SkeletonGrid columns={1} rows={1} height={20} />
        <SkeletonGrid columns={3} rows={1} height={30} />
        <SkeletonGrid columns={4} rows={1} height={30} />
      </>
    );
  }

  if (isError) return <div>Error loading patient settings</div>;
  if (!patientSettings) return <div>No patient settings available</div>;

  return (
    <div>
      <div className={styles.buttonContainer}>
        {isEditing ? (
          <Button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={isUpdating}
            loading={isUpdating}>
            {isUpdating ? 'Saving...' : 'Save'}
          </Button>
        ) : (
          !isPractitioner && (
            <IconButton
              label="Edit"
              icon="edit"
              onClick={handleEdit}
              className={styles.editBtn}
              disabled={isUpdating}
            />
          )
        )}
      </div>

      {renderFieldGrid(monitoringFields)}
      {renderFieldGrid(cycleFields)}
    </div>
  );
};

export default SettingsSection;
